import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts';
import {
  consultarSituacaoApuracaoAssistida,
  fetchUltimaApuracaoAssistida,
  solicitarApuracaoAssistida,
} from '../api/ApuracaoAssistidaCBSApi';

function useCnpjGestor() {
  const { user } = useAuth();
  return user?.cnpj ?? '';
}

export function useUltimaApuracaoAssistida(cnpjContribuinte: string, habilitado = true) {
  const cnpjGestor = useCnpjGestor();

  return useQuery({
    queryKey: ['apuracao-assistida-cbs', 'ultima', cnpjGestor, cnpjContribuinte],
    queryFn: () => fetchUltimaApuracaoAssistida(cnpjContribuinte),
    staleTime: 60 * 1000,
    enabled: habilitado && !!cnpjGestor && !!cnpjContribuinte,
  });
}

export function useSolicitarApuracaoAssistida() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cnpjContribuinte: string) => solicitarApuracaoAssistida(cnpjContribuinte),
    onSuccess: (response, cnpjContribuinte) => {
      if (response.status.resultado) {
        toast({
          title: 'Solicitação protocolada na Receita',
          description: response.status.mensagem || 'O resultado fica disponível em até 4 horas.',
        });
        queryClient.invalidateQueries({
          queryKey: ['apuracao-assistida-cbs', 'ultima', cnpjGestor, cnpjContribuinte],
        });
      } else {
        // O motivo da recusa (SPEC-019) é um parágrafo: vai na descrição, não no título.
        toast({
          variant: 'destructive',
          // Neutro de propósito: nem toda recusa é da Receita (sem credencial, sem CNPJ).
          title: 'O pedido não foi atendido.',
          description: response.status.mensagem,
        });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao solicitar a apuração na Receita.' });
    },
  });
}

export function useConsultarSituacaoApuracaoAssistida() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cnpj, tiquete }: { cnpj: string; tiquete: string }) =>
      consultarSituacaoApuracaoAssistida(cnpj, tiquete),
    onSuccess: (response, { cnpj }) => {
      if (!response.status.resultado) {
        // O motivo da recusa (SPEC-019) é um parágrafo: vai na descrição, não no título.
        toast({
          variant: 'destructive',
          // Neutro de propósito: nem toda recusa é da Receita (sem credencial, sem CNPJ).
          title: 'O pedido não foi atendido.',
          description: response.status.mensagem,
        });
        return;
      }

      const estado = response.data?.Estado;

      if (estado === 'CONCLUIDA') {
        toast({ title: 'Apuração recebida da Receita.' });
        queryClient.invalidateQueries({
          queryKey: ['apuracao-assistida-cbs', 'ultima', cnpjGestor, cnpj],
        });
      } else if (estado === 'ERRO') {
        toast({
          variant: 'destructive',
          title: 'A Receita recusou a solicitação',
          description: response.data?.MensagemErro ?? undefined,
        });
      } else {
        toast({
          title: 'Ainda em processamento na Receita',
          description: 'A solicitação leva até 4 horas. Consulte de novo mais tarde.',
        });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao consultar a situação na Receita.' });
    },
  });
}
